/**
 * Error code for generic/unexpected errors.
 * The frontend translates this code to a localized, high-level message.
 */
export const ERROR_CODE_UNEXPECTED = 'ERROR_UNEXPECTED';
/**
 * Error code for a provider that would not answer: throttled, failing, or
 * silent past the inactivity watchdog. The advice is the same for all of
 * these — wait and retry — and the end user never sees which one it was.
 */
export const ERROR_CODE_PROVIDER_UNAVAILABLE = 'ERROR_PROVIDER_UNAVAILABLE';
/**
 * Error code for a sandbox the turn could not get ready: not started, not
 * resumed, or not prepared. The advice is the same as for a provider: wait
 * and retry.
 */
export const ERROR_CODE_SANDBOX_UNAVAILABLE = 'ERROR_SANDBOX_UNAVAILABLE';
/**
 * Encode a payload into the string carried by an error chunk's `errorText`.
 */
export function encodeStreamError(payload) {
    return JSON.stringify(payload);
}
/**
 * Recognizes a server error code (the `ERROR_*` family). Used identically for
 * the JSON form and the bare-code form so both encodings of the same logical
 * code yield the same recognition.
 */
const SERVER_ERROR_CODE_PATTERN = /^ERROR_[A-Z\d_]+$/;
/**
 * Decode an error chunk's `errorText` back into a payload.
 *
 * Returns `undefined` when the text is not a recognized server error code
 * (e.g. a client-side transport error message), so callers can distinguish a
 * server-delivered error from a transport failure.
 */
export function decodeStreamError(errorText) {
    try {
        const parsed = JSON.parse(errorText);
        if (typeof parsed === 'object'
            && parsed !== null
            && 'code' in parsed
            && typeof parsed.code === 'string'
            && SERVER_ERROR_CODE_PATTERN.test(parsed.code)) {
            const { code, explanation } = parsed;
            return {
                code,
                explanation: typeof explanation === 'string' ? explanation : undefined
            };
        }
    }
    catch {
        // Not JSON — fall through to the bare-code form.
    }
    // Bare code form (e.g. legacy chunks carrying just the code string).
    if (SERVER_ERROR_CODE_PATTERN.test(errorText)) {
        return { code: errorText };
    }
    return undefined;
}
